import OrderTable from '@shared/tables/order-table'
import ProfileWidget from '@shared/profile-widget'
import React, { useEffect, useState } from 'react'
import Tabs from '@shared/tabs'
import hasPerms from '@utils/has-permission'
import {
  CircularProgress,
  List,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material'
import { formatPhoneNumber } from '@utils/number-utils'
import { getOrdersByUser } from '@queries/orders'
import { makeStyles } from 'tss-react/mui'
import { selectPermissions, selectRoles, selectStores } from '@store/selectors'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

import CCPA from './ccpa'
import Credits from './credits'
import CroppedImageInput from '@components/shared/cropped-image-input'
import DemographicsChanges from './demographics-changes'
import PasswordResetLink from './password-reset-link'
import getImage from '@utils/get-image'
import useAllActions from '@hooks/use-all-actions'
import useSelector from '@hooks/use-selector'
import { AnyAction } from 'redux'
import { OrderList, UserState } from '@types'
import { getUser, removePhotoUser, uploadPhotoUser } from '@store/actions/users'
import { selectUserById } from '@store/selectors/users'

const User = () => {
  const dispatch = useDispatch()
  const { classes } = useStyles()
  const { id } = useParams()
  const theme = useTheme()
  const { getRoles, getStores } = useAllActions()
  const navigate = useNavigate()

  const user = useSelector((state) => selectUserById(state, id))
  const roles = useSelector(selectRoles)
  const permissions: any = useSelector(selectPermissions)
  const stores = useSelector(selectStores)
  const currentUserId = useSelector((state) => state.CurrentUser.user.id)

  const showCCPA = hasPerms('customers:manage-ccpa')
  const showPasswordReset = hasPerms('customers:create-password-reset-link')
  const canUpdateUsers = hasPerms('users:update')
  const canEditPhoto = hasPerms('users:update-photo')
  const canUpdateManagerApprovalCode = hasPerms(
    'users:update-manager_approval_code',
  )
  const canViewManagerApprovalCode =
    canUpdateManagerApprovalCode || (user?.id && currentUserId == user?.id)

  const [orders, setOrders] = useState<OrderList>([])
  const [ordersCount, setOrdersCount] = useState(0)

  const fetchOrders = async (page, pageSize, begin = null, end = null) => {
    const { orders, total_results: ordersCount } = await getOrdersByUser(
      parseInt(id as any, 10),
      page,
      pageSize,
      begin,
      end,
    )
    setOrders(orders)
    setOrdersCount(ordersCount)
  }

  useEffect(() => {
    getRoles()
    dispatch(getUser(id as any) as unknown as AnyAction)
    getStores()
  }, [])

  const permissionsLoaded = Object.keys(permissions).length > 0

  if (!permissionsLoaded || !user || !roles || !stores)
    return <CircularProgress />

  const isGuestUser = user.state === UserState.guest
  const isInstoreUser = user.state === UserState.instore

  const pageTabs: any = []

  pageTabs.push({
    title: 'Orders',
    component: (
      <OrderTable
        emptyChild={<h2>No Orders Placed</h2>}
        fetchOrders={fetchOrders}
        includeStore
        orders={orders}
        ordersCount={ordersCount}
        routeForOrder={(orderId) => `/users/${id}/orders/${orderId}`}
      />
    ),
  })

  if (!isGuestUser) {
    if (!isInstoreUser) {
      pageTabs.push({
        title: 'Credits',
        component: <Credits user={user} />,
      })
    }

    if (showCCPA) {
      pageTabs.push({
        title: 'CCPA',
        component: <CCPA user={user} />,
      })
    }

    if (showPasswordReset && !isInstoreUser) {
      pageTabs.push({
        title: 'Password Reset',
        component: <PasswordResetLink user={user} />,
      })
    }

    pageTabs.push({
      title: 'Demographics Change Log',
      component: <DemographicsChanges user={user} />,
    })
  }

  const userRoles = () => {
    const empRoles = user.role_ids?.map((roleId) => {
      return roles[roleId]
    })

    return empRoles?.sort((a, b) => (a.role > b.role ? 1 : -1))
  }

  const userStores = () => {
    const empStores = user.store_ids?.map((storeId) => {
      return stores[storeId]
    })

    return empStores?.sort((a, b) => (a.name > b.name ? 1 : -1))
  }

  const userPermissions = () => {
    const empPerms = user.permission_ids?.map((permId) => {
      return permissions[permId]
    })

    return empPerms?.sort((a, b) => (a.permission > b.permission ? 1 : -1))
  }
  const hasAllStoresPermission = !!Object.values(userPermissions() || {}).find(
    (p: any) => p.permission == 'stores:update-all',
  )

  const handlePhotoChange = async (url) => {
    const imageData = await getImage(url)

    await dispatch(uploadPhotoUser(user.id, imageData) as unknown as AnyAction)

    navigate(`/users/${user.id}`)
  }

  const handlePhotoRemove = async () => {
    await dispatch(removePhotoUser(user.id) as unknown as AnyAction)

    navigate(`/users/${user.id}`)
  }

  const empBody = !isGuestUser && (
    <div className={classes.section}>
      <div className={classes.block}>
        <CroppedImageInput
          desiredHeight={600}
          desiredWidth={600}
          imageUrl={user.photo_url}
          isEditing={canEditPhoto}
          onChange={handlePhotoChange}
          onRemove={handlePhotoRemove}
        />
      </div>
      <div className={classes.block}>
        <Typography variant='h6'>Roles</Typography>
        <List>
          {userRoles()?.map((role) => {
            return (
              role && (
                <ListItemText
                  key={`role_${role?.id || 0}`}
                  primary={role?.role}
                />
              )
            )
          })}
        </List>
      </div>
      <div className={classes.block}>
        <Typography variant='h6'>Permissions</Typography>
        <List>
          {userPermissions()?.map((perm) => {
            return (
              perm && (
                <ListItemText
                  key={`permission_${perm?.id || Math.random()}`}
                  primary={perm?.permission}
                />
              )
            )
          })}
        </List>
      </div>
      <div className={classes.block}>
        <Typography variant='h6'>Stores</Typography>
        <List>
          {hasAllStoresPermission ? (
            <ListItemText key='store_all' primary='All Stores' />
          ) : (
            userStores()?.map((store) => {
              return (
                store && (
                  <ListItemText
                    key={`store_${store?.id || Math.random()}`}
                    primary={store?.name}
                  />
                )
              )
            })
          )}
        </List>
      </div>
    </div>
  )

  const basicInfoList = isGuestUser ? null : (
    <dl className={classes.descriptiveList}>
      <dt>Phone Number</dt>
      <dd>{formatPhoneNumber(user.phone_number) || 'Unavailable'}</dd>
      <dt>Loyalty Number</dt>
      <dd>{user.loyalty_number || 'Not Enabled'}</dd>
      <dt>Birth Date</dt>
      <dd>{user.birthdate || 'Not Set'}</dd>
      {user.manager_approval_code && canViewManagerApprovalCode && (
        <>
          <dt>Manager Approval Code</dt>
          <dd>{user.manager_approval_code}</dd>
        </>
      )}
    </dl>
  )

  const buttons =
    !isGuestUser && canUpdateUsers
      ? [
          {
            title: 'Edit',
            onClick: () => navigate(`/users/${id}/edit`),
            color: theme.palette.primary.main,
          },
        ]
      : []

  return (
    <div>
      <ProfileWidget
        body={empBody}
        bodyPrefix={basicInfoList}
        buttons={buttons}
        subtitle1={user.email || 'Email address unavailable'}
        title={user.name}
      />
      {pageTabs.length > 0 && <Tabs tabs={pageTabs} />}
    </div>
  )
}

const useStyles = makeStyles()((theme) => ({
  section: {
    marginBottom: theme.spacing(2),
    display: 'flex',
  },
  block: {
    marginRight: theme.spacing(2),
    padding: theme.spacing(2),
    width: 'auto',
  },
  descriptiveList: {
    display: 'grid',
    gridTemplateColumns: 'max-content',
    dt: {
      '&::after': {
        display: 'inline',
        content: '":"',
      },
    },
    dd: {
      gridColumnStart: 2,
      marginInlineStart: '10px',
    },
  },
}))

export default User
